import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import StaffToolbar from "../components/staff/StaffToolbar";
import StaffTable from "../components/staff/StaffTable";
import StaffFormModal from "../components/staff/StaffFormModal";
import ViewStaffModal from "../components/staff/ViewStaffModal";
import { subscribeToStaff, setCommunicationCentreAccess } from "../services/staffService";
import {
  createStaffUserAccount,
  setStaffUserActive,
  updateStaffUserAccount,
} from "../services/authAdminService";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function StaffMaster() {
  const { userData } = useAuth();
  const role = String(userData?.role || "").toLowerCase();
  const canAccess = role === "admin" || role === "partner";
  const canEdit = role === "admin";

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formState, setFormState] = useState({ open: false, mode: "add", staff: null });
  const [viewingStaff, setViewingStaff] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canAccess) return undefined;
    return subscribeToStaff(
      (rows) => {
        setStaff(rows);
        setLoading(false);
      },
      (error) => {
        console.error("Staff listener failed:", error);
        alert(`Unable to load staff: ${error.message}`);
        setLoading(false);
      }
    );
  }, [canAccess]);

  const filteredStaff = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return staff.filter((member) => {
      if (statusFilter !== "all" && member.status !== statusFilter) return false;
      if (!term) return true;
      return [member.name, member.username, member.email, member.mobile, member.role]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [staff, searchTerm, statusFilter]);

  if (userData && !canAccess) return <Navigate to="/" replace />;

  const saveStaff = async (value) => {
    setSaving(true);
    try {
      if (formState.mode === "add") {
        if (staff.some((member) => String(member.email || "").toLowerCase() === value.email)) {
          alert("A staff profile already exists for this email address.");
          return;
        }
        const created = await createStaffUserAccount(value);
        if (created?.uid) await setCommunicationCentreAccess(created.uid, value.role === "partner" && value.communicationCentreEnabled === true);
        alert(`Staff user created successfully. A password setup email has been sent to ${value.email}.`);
      } else {
        await updateStaffUserAccount(formState.staff.id, value);
        await setCommunicationCentreAccess(formState.staff.id, value.role === "partner" && value.communicationCentreEnabled === true);
      }
      setFormState({ open: false, mode: "add", staff: null });
    } catch (error) {
      console.error("Staff save failed:", error);
      alert(`Unable to save staff: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (member) => {
    const currentlyInactive = member.status === "inactive";
    if (currentlyInactive) {
      if (!window.confirm(`Reactivate ${member.name || member.email}?`)) return;
      try {
        await setStaffUserActive(member.id, true);
      } catch (error) {
        alert(`Unable to reactivate staff: ${error.message}`);
      }
      return;
    }

    const leavingDate = window.prompt(
      `Enter the leaving date for ${member.name || member.email} in YYYY-MM-DD format:`,
      todayIso()
    );
    if (leavingDate === null) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(leavingDate)) {
      alert("Enter the leaving date in YYYY-MM-DD format.");
      return;
    }
    if (!window.confirm(`Mark ${member.name || member.email} as inactive from ${leavingDate}?`)) return;

    try {
      await setStaffUserActive(member.id, false, leavingDate);
    } catch (error) {
      alert(`Unable to deactivate staff: ${error.message}`);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold">Staff Master</h1>
        <p className="text-gray-600 mt-1">Maintain staff details, roles, employment dates and active status.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="bg-white border rounded-lg p-4"><div className="text-sm text-gray-500">Total Staff</div><div className="text-2xl font-semibold">{staff.length}</div></div>
        <div className="bg-white border rounded-lg p-4"><div className="text-sm text-gray-500">Active</div><div className="text-2xl font-semibold">{staff.filter((item) => item.status !== "inactive").length}</div></div>
        <div className="bg-white border rounded-lg p-4"><div className="text-sm text-gray-500">Inactive</div><div className="text-2xl font-semibold">{staff.filter((item) => item.status === "inactive").length}</div></div>
      </div>

      <StaffToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        canEdit={canEdit}
        onAdd={() => setFormState({ open: true, mode: "add", staff: null })}
      />

      <StaffTable
        staff={filteredStaff}
        loading={loading}
        canEdit={canEdit}
        onView={setViewingStaff}
        onEdit={(member) => setFormState({ open: true, mode: "edit", staff: member })}
        onToggleStatus={toggleStatus}
      />

      <StaffFormModal
        open={formState.open}
        mode={formState.mode}
        staff={formState.staff}
        saving={saving}
        onClose={() => !saving && setFormState({ open: false, mode: "add", staff: null })}
        onSave={saveStaff}
      />
      <ViewStaffModal staff={viewingStaff} onClose={() => setViewingStaff(null)} />
    </div>
  );
}
